import { CLIP, QUALIFIERS } from '../../enums';

export type FilterField = 'row' | 'qualifier' | 'category' | 'notes' | 'comments' | 'text' | 'commenter';
export type SearchField = Exclude<FilterField, 'text' | 'commenter'>;
export type FilterOperator = 'contains' | 'startsWith' | 'endsWith' | 'is' | 'isNot';
export type FilterToken = { field: FilterField; fields?: SearchField[]; operator: FilterOperator; value: string; label?: string; color?: string };
export type FilterConfig = { tokens: FilterToken[]; match: 'all' | 'any'; mode: 'in' | 'out' };
export type SavedClipFilter = { id: string | number; name: string; config: FilterConfig };
export type FilterRow = { id: string; name: string; details?: { color?: string }; clips: (CLIP & { qualifiers?: QUALIFIERS[] })[] };
export type FilterComment = {
  id: string | number;
  clipId: string | number;
  message: string;
  deleted_at?: string | null;
  author: { id: string | number; username: string; avatar_url: string | null } | null;
};
export type FilterValue = { value: string; count: number; color?: string };

export const emptyFilter = (): FilterConfig => ({ tokens: [], match: 'all', mode: 'in' });
export const filterFields = { row: 'Row Name', qualifier: 'Qualifier Name', category: 'Qualifier Category', notes: 'Notes', comments: 'Comments', text: 'Notes & Comments', commenter: 'Commented by' };
export const filterOperators: { value: FilterOperator; label: string }[] = [
  { value: 'contains', label: 'Contains' }, { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' }, { value: 'is', label: 'Is' }, { value: 'isNot', label: 'Is not' },
];
const abbreviations: Record<FilterField, string> = { row: 'RN', qualifier: 'QN', category: 'QC', notes: 'Notes', comments: 'Comments', text: 'Text', commenter: 'By' };
export const tokenLabel = (token: FilterToken) => {
  if (token.field === 'commenter') return `By: ${token.value === '*' ? 'Any commenter' : token.label || token.value}`;
  const fields = (token.fields || [token.field]).map((field) => abbreviations[field]).join('/');
  return token.operator === 'is' ? `${fields}: ${token.value}`
    : `${fields} ${filterOperators.find((operator) => operator.value === token.operator)?.label.toLowerCase()} “${token.value}”`;
};
const tokenFields = (token: FilterToken): SearchField[] => token.field === 'text'
  ? token.fields ?? ['notes', 'comments'] : token.field === 'commenter' ? [] : [token.field];
export const tokenNeedsComments = (token: FilterToken) => token.field === 'commenter' || tokenFields(token).includes('comments');
export const tokenKey = (token: FilterToken) => JSON.stringify([
  token.field === 'commenter' ? token.field : tokenFields(token).slice().sort(), token.operator, token.value.trim().toLowerCase(),
]);

export const matchesText = (values: (string | null | undefined)[], operator: FilterOperator, query: string) => {
  const needle = query.trim().toLowerCase();
  const normalized = values.filter((value): value is string => typeof value === 'string').map((value) => value.trim().toLowerCase());
  if (operator === 'isNot') return normalized.every((value) => value !== needle);
  return normalized.some((value) => {
    if (operator === 'is') return value === needle;
    if (operator === 'startsWith') return value.startsWith(needle);
    if (operator === 'endsWith') return value.endsWith(needle);
    return value.includes(needle);
  });
};

const clipQualifiers = (clip: FilterRow['clips'][number]) => (clip.qualifiers || [])
  .filter((qualifier) => qualifier.time >= clip.time_start && qualifier.time <= clip.time_end);

export const collectFilterValues = (rows: FilterRow[]) => {
  const groups: Record<'row' | 'qualifier' | 'category', Map<string, FilterValue>> = {
    row: new Map(), qualifier: new Map(), category: new Map(),
  };
  const add = (field: keyof typeof groups, value: string | undefined, count: number, color?: string) => {
    if (!value?.trim()) return;
    const key = value.trim().toLowerCase();
    const previous = groups[field].get(key);
    if (previous) previous.count += count;
    else groups[field].set(key, { value: value.trim(), count, color });
  };
  rows.forEach((row) => {
    add('row', row.name, row.clips.length, row.details?.color || row.clips[0]?.color);
    row.clips.forEach((clip) => {
      const names = new Set<string>();
      const categories = new Set<string>();
      clipQualifiers(clip).forEach((qualifier) => {
        const name = qualifier.name?.trim().toLowerCase();
        if (name && !names.has(name)) { names.add(name); add('qualifier', qualifier.name, 1, qualifier.color); }
        const category = qualifier.category?.trim().toLowerCase();
        if (category && !categories.has(category)) { categories.add(category); add('category', qualifier.category || undefined, 1, qualifier.color); }
      });
    });
  });
  const sort = (values: Map<string, FilterValue>) => Array.from(values.values()).sort((a, b) => a.value.localeCompare(b.value));
  return { row: sort(groups.row), qualifier: sort(groups.qualifier), category: sort(groups.category) };
};

export const evaluateFilter = (rows: FilterRow[], config: FilterConfig, comments: FilterComment[], commentsReady: boolean) => {
  const dimmedIds = new Set<string>();
  const collapsedRowIds = new Set<string>();
  const total = rows.reduce((count, row) => count + row.clips.length, 0);
  const needsComments = config.tokens.some(tokenNeedsComments);
  if (!config.tokens.length || (needsComments && !commentsReady)) return { dimmedIds, collapsedRowIds, total, shown: total };
  const commentsByClip = new Map<string, FilterComment[]>();
  comments.forEach((comment) => {
    const id = String(comment.clipId);
    const list = commentsByClip.get(id) || [];
    list.push(comment);
    commentsByClip.set(id, list);
  });
  rows.forEach((row) => {
    let shown = 0;
    row.clips.forEach((clip) => {
      const qualifiers = clipQualifiers(clip);
      const clipComments = commentsByClip.get(String(clip.id)) || [];
      const matches = (token: FilterToken) => {
        if (token.field === 'commenter') return clipComments.some((comment) => token.value === '*' || String(comment.author?.id) === token.value);
        const values = tokenFields(token).flatMap((field) => field === 'row' ? [row.name]
          : field === 'qualifier' ? qualifiers.map((qualifier) => qualifier.name)
          : field === 'category' ? qualifiers.map((qualifier) => qualifier.category || '').filter(Boolean)
          : field === 'notes' ? (clip.notes || []).map((note) => note.note)
          : clipComments.map((comment) => comment.message));
        return matchesText(values, token.operator, token.value);
      };
      const match = config.match === 'all' ? config.tokens.every(matches) : config.tokens.some(matches);
      if (config.mode === 'in' ? match : !match) shown += 1;
      else dimmedIds.add(String(clip.id));
    });
    if (!shown) collapsedRowIds.add(String(row.id));
  });
  return { dimmedIds, collapsedRowIds, total, shown: total - dimmedIds.size };
};
